import anaUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import linkTrackingError from 'analytics/bindings/pages/all/linkTrackingError';

const { SOT_LINK_TRACKING_EVENT, LINK_TRACKING_EVENT, EVENT_NAMES } = anaConsts;
const { EMAIL_SENT, EMAIL_RESENT } = EVENT_NAMES.SIGN_IN;

/**
 * ResetPassword-page analytics helpers.
 *
 * Analytics parity with `components/GlobalModals/ForgotPasswordModal/
 * ForgotPasswordModal.ctrlr.jsx` and its resend companion
 * `viewModel/globalModals/resetPasswordConfirmationModal/
 * withResetPasswordConfirmationModalProps.js`. Consolidated here so the three
 * page screens (TraditionalResetForm, PasskeyFirstLanding, EmailSentConfirmation)
 * share the same envelope the modal fires.
 *
 * Every `processEvent.process` dispatch is wrapped in a safe getBindingMethods
 * pre-warm — under jsdom the fire-and-forget `import('analytics/getBindingMethods')`
 * inside `processEvent` can synthesize an unhandled rejection that crashes the
 * jest worker. Matches the safe-dispatch pattern already used in
 * `SetNewPassword.f.jsx` (see comment at line ~200 of that file).
 */

const dispatchAnalyticsSafely = (eventName, payload) => {
    return import(/* webpackMode: "eager" */ 'analytics/getBindingMethods')
        .then(() => processEvent.process(eventName, payload))
        .catch(analyticsError => {
            if (typeof window !== 'undefined') {
                window.Sephora?.logger?.warn?.('[Analytics] ResetPassword event dispatch failed', {
                    event: eventName,
                    error: analyticsError?.message || analyticsError
                });
            }
        });
};

/**
 * Fires the field-level validation error event.
 * Mirrors ForgotPasswordModal.isValid (~line 64): event140/141 with
 * prop43:'manual' + fieldErrors + last async page-load context.
 */
export const trackForgotPasswordValidationError = fieldErrors => {
    if (!fieldErrors?.length) {
        return;
    }

    dispatchAnalyticsSafely(LINK_TRACKING_EVENT, {
        data: {
            bindingMethods: linkTrackingError,
            eventStrings: ['event140', 'event141'],
            prop43: 'manual',
            fieldErrors,
            ...anaUtils.getLastAsyncPageLoadData()
        }
    });
};

/**
 * Fires the send-failure event.
 * Mirrors ForgotPasswordModal.submit onFailure (~line 161): guarded on
 * response.errorMessages — no event if the response omits errorMessages.
 */
export const trackForgotPasswordSendFailure = errorMessages => {
    if (!errorMessages) {
        return;
    }

    dispatchAnalyticsSafely(LINK_TRACKING_EVENT, {
        data: {
            bindingMethods: linkTrackingError,
            errorMessages,
            eventStrings: ['event140', 'event141'],
            prop43: 'manual',
            ...anaUtils.getLastAsyncPageLoadData()
        }
    });
};

/**
 * Fires a SOT_LINK_TRACKING_EVENT envelope identical to
 * `ForgotPasswordBindings.triggerSOTAnalytics` — replicated inline so we can
 * route through the safe dispatch wrapper. Keeps the linkName/actionInfo/
 * specificEventName trio the modal emits verbatim.
 */
const dispatchSOTLinkTrackingSafely = (eventName, extraData) => {
    dispatchAnalyticsSafely(SOT_LINK_TRACKING_EVENT, {
        data: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            ...extraData
        }
    });
};

/**
 * Fires the email-sent SOT event AND primes the next page-load envelope.
 * Mirrors ForgotPasswordModal.submit onSuccess (~lines 146–148):
 *   - `ForgotPasswordBindings.emailSent({ emailId })` (SOT_LINK_TRACKING_EVENT)
 *   - `anaUtils.setNextPageData({ events: ['event140','event100'], prop43:'manual' })`
 * Applies to both traditional and passkey success paths — the modal fires
 * the SOT emailSent from both `submit` and `handleCreatePasskey`.
 */
export const trackForgotPasswordEmailSent = email => {
    dispatchSOTLinkTrackingSafely(EMAIL_SENT, { emailId: email });
    anaUtils.setNextPageData({ events: ['event140', 'event100'], prop43: 'manual' });
};

/**
 * Fires the resend SOT event.
 * Mirrors `emailResentTracking` wiring in
 * withResetPasswordConfirmationModalProps.js:40 (SOT_LINK_TRACKING_EVENT
 * with `SIGN_IN.EMAIL_RESENT`), invoked from
 * ResetPasswordConfirmationModal.sendEmail successCallback (~line 24).
 */
export const trackForgotPasswordEmailResent = email => {
    dispatchSOTLinkTrackingSafely(EMAIL_RESENT, { emailId: email });
};
